import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, X, XCircle } from "lucide-react";
import { getKeys, saveKeys, testEmail, testProspectProvider, checkCalling, aiTest } from "../lib/api";
import KeyFields from "./KeyFields";

const errText = (e, fallback) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : fallback; };

// Each test returns [{ok, text}] lines to show under the Test button.
const emailLines = (r) => [["Sending", r.smtp], ["Reply detection", r.imap]].map(([n, x]) => ({ ok: !!x.ok, text: `${n}: ${x.ok ? "connected" : x.error}` }));
const detailLines = (r) => [{ ok: !!r.ok, text: r.detail }];
const callLines = (list) => list.filter(c => c.ok !== null).map(c => ({ ok: !!c.ok, text: `${c.name}: ${c.detail}` }));

export const STEPS = [
  { id: "email", title: "Send email from your own address", why: "Sequences and your own emails go out from your mailbox, and replies are detected so a sequence stops when someone answers. Use an app password, not your normal one.",
    groups: ["Email (sending + reply detection)", "Email signature"], test: async () => emailLines(await testEmail()) },
  { id: "timing", title: "When emails go out", why: "By default emails go out any time. Pick hours only if your recipients should not be emailed at night.",
    groups: ["When emails are sent"], test: null },
  { id: "leads", title: "Find new prospects", why: "Search for people by job title, industry and location, and reveal their email addresses.",
    groups: ["Prospect data"], test: async () => detailLines(await testProspectProvider()) },
  { id: "calls", title: "AI phone calls", why: "An AI agent calls the people you choose and saves how each call went. Interested callers become contacts for you to approve.",
    groups: ["AI voice calling", "Phone numbers (calling through Vapi)"], test: async () => callLines(await checkCalling()) },
  { id: "ai", title: "AI writing for social posts", why: "Drafts posts from your own material, or from a few answers about your business. You approve every post.",
    groups: ["AI writing for social posts"], test: async () => detailLines(await aiTest()) },
];

// A short guided tour of the keys a company may want. Every step can be skipped and is saved on its own, so
// closing it half-way loses nothing. Opens after first-time setup, and from Settings at any time.
export default function SetupWizard({ close }) {
  const [index, setIndex] = useState(0);
  const [saved, setSaved] = useState({});
  const [values, setValues] = useState({});
  const [lines, setLines] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState({});   // step id -> "tested" | "saved" | "skipped"

  useEffect(() => { getKeys().then(setSaved).catch(() => {}); }, []);
  const step = STEPS[index];
  const finished = index >= STEPS.length;

  const go = (status) => { setDone(d => ({ ...d, [step.id]: status })); setValues({}); setLines(null); setIndex(i => i + 1); };

  const saveAndTest = async () => {
    setBusy(true); setLines(null);
    try {
      const filled = Object.fromEntries(Object.entries(values).filter(([, v]) => v !== undefined && String(v).trim() !== ""));
      if (Object.keys(filled).length) { setSaved(await saveKeys(filled)); setValues({}); }
      if (!step.test) { toast.success("Saved"); go("saved"); return; }
      setLines(await step.test());
    } catch (e) { setLines([{ ok: false, text: errText(e, "That didn't work — check the details and try again") }]); }
    finally { setBusy(false); }
  };
  const passed = lines && lines.length > 0 && lines.every(l => l.ok);

  return <div className="modal-backdrop"><div className="modal login-card setup-card wizard" data-testid="setup-wizard">
    <button className="modal-close" onClick={close} title="Close — you can come back from Settings"><X size={16} /></button>
    {!finished ? <>
      <div className="wizard-dots">{STEPS.map((s, i) => <span key={s.id} className={i === index ? "on" : done[s.id] ? "done" : ""} />)}</div>
      <small className="muted">Step {index + 1} of {STEPS.length}</small>
      <h2>{step.title}</h2>
      <p>{step.why}</p>
      <KeyFields values={values} saved={saved} only={step.groups} onChange={(k, v) => setValues(s => ({ ...s, [k]: v }))} />
      {lines && <div className="wizard-result" data-testid="wizard-result">{lines.map((l, i) => <div key={i} className={l.ok ? "ok" : "bad"}>{l.ok ? <Check size={13} /> : <XCircle size={13} />} {l.text}</div>)}</div>}
      <div className="wizard-actions">
        <button className="outline-button" onClick={() => go("skipped")} disabled={busy} data-testid="wizard-skip">Skip for now</button>
        {passed
          ? <button className="primary-button" onClick={() => go("tested")} data-testid="wizard-next">Next</button>
          : <button className="primary-button" onClick={saveAndTest} disabled={busy} data-testid="wizard-test">{busy ? <Loader2 size={14} className="spin" /> : null}{step.test ? "Save and test" : "Save"}</button>}
      </div>
    </> : <>
      <h2>You're set</h2>
      <p>You can change any of this later in Settings, and reopen this guide from there.</p>
      <ul className="wizard-summary">{STEPS.map(s => <li key={s.id} className={done[s.id] === "skipped" || !done[s.id] ? "skip" : "ok"}>
        {done[s.id] === "skipped" || !done[s.id] ? <XCircle size={14} /> : <Check size={14} />} {s.title} <small>{done[s.id] === "tested" ? "connected" : done[s.id] === "saved" ? "saved" : "skipped"}</small></li>)}</ul>
      <p>Social accounts (Facebook, Instagram, LinkedIn and more) connect under <b>Social → Accounts</b>.</p>
      <button className="primary-button full" onClick={close} data-testid="wizard-finish">Start using the CRM</button>
    </>}
  </div></div>;
}
